import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

interface InvoiceItem {
  service_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  discount?: number;
  gst_amount?: number;
}

interface InvoiceSale {
  invoice_number: string | null;
  created_at: string;
  customer_name: string | null;
  customer_phone: string | null;
  total_amount: number;
  total_discount?: number;
  received_amount: number;
  due_amount: number;
  payment_mode: string;
  payment_status: string;
  notes?: string | null;
}

interface InvoiceTenant {
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  gstin?: string | null;
  logo_url?: string | null;
  vle_id?: string | null;
}

interface InvoiceService {
  name: string;
  description?: string;
}

interface InvoiceSettings {
  gst_enabled?: boolean;
  apply_gst_on_invoices?: boolean;
  cgst_percent?: number;
  sgst_percent?: number;
  show_logo?: boolean;
  bank_details?: string | null;
  upi_id?: string | null;
  terms_conditions?: string | null;
  invoice_footer?: string | null;
  qr_code_url?: string | null;
  signature_url?: string | null;
  stamp_enabled?: boolean;
  invoice_services?: InvoiceService[] | null;
  branding_text?: string | null;
  branding_enabled?: boolean;
}

// Number to words conversion for Indian currency
function numberToWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero';

  const convertLessThanThousand = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };

  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);

  let result = '';
  let remaining = rupees;
  
  if (remaining >= 10000000) {
    result += convertLessThanThousand(Math.floor(remaining / 10000000)) + ' Crore ';
    remaining = remaining % 10000000;
  }
  if (remaining >= 100000) {
    result += convertLessThanThousand(Math.floor(remaining / 100000)) + ' Lakh ';
    remaining = remaining % 100000;
  }
  if (remaining >= 1000) {
    result += convertLessThanThousand(Math.floor(remaining / 1000)) + ' Thousand ';
    remaining = remaining % 1000;
  }
  if (remaining > 0) {
    result += convertLessThanThousand(remaining);
  }

  result = result.trim() + ' Rupees';
  
  if (paise > 0) {
    result += ' and ' + convertLessThanThousand(paise) + ' Paise';
  }
  
  return result + ' Only';
}

// Generate stamp image on canvas and return data URL
function generateStampDataUrl(businessName: string, cscId: string, address: string): string {
  const canvas = document.createElement('canvas');
  const size = 150;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) return '';
  
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 8;
  
  // Use orange color like reference image
  const primaryColor = '#ea580c';
  
  ctx.clearRect(0, 0, size, size);
  
  // Outer circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 3;
  ctx.stroke();
  
  // Inner circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius - 10, 0, 2 * Math.PI);
  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 1;
  ctx.stroke();
  
  // Top curved text
  ctx.save();
  ctx.font = 'bold 11px Arial';
  ctx.fillStyle = primaryColor;
  ctx.textAlign = 'center';
  
  const topText = '• Digital Seva Kendra •';
  const topRadius = radius - 20;
  const topStartAngle = Math.PI + 0.35;
  const topAngleStep = (Math.PI - 0.7) / topText.length;
  
  for (let i = 0; i < topText.length; i++) {
    const angle = topStartAngle + i * topAngleStep;
    const x = centerX + topRadius * Math.cos(angle);
    const y = centerY + topRadius * Math.sin(angle);
    
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle + Math.PI / 2);
    ctx.fillText(topText[i], 0, 0);
    ctx.restore();
  }
  ctx.restore();
  
  // Bottom curved text - Address
  ctx.save();
  ctx.font = '9px Arial';
  ctx.fillStyle = primaryColor;
  
  const bottomText = address.length > 25 ? address.slice(0, 25) : address;
  const bottomRadius = radius - 20;
  const bottomStartAngle = -0.25;
  const bottomAngleStep = (Math.PI - 0.5) / Math.max(bottomText.length, 1);
  
  for (let i = 0; i < bottomText.length; i++) {
    const angle = bottomStartAngle + i * bottomAngleStep;
    const x = centerX + bottomRadius * Math.cos(angle);
    const y = centerY + bottomRadius * Math.sin(angle);
    
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle - Math.PI / 2);
    ctx.fillText(bottomText[i], 0, 0);
    ctx.restore();
  }
  ctx.restore();
  
  // Center CSC
  ctx.font = 'bold 18px Arial';
  ctx.fillStyle = primaryColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('C|S|C', centerX, centerY - 16);
  
  // Horizontal lines
  ctx.beginPath();
  ctx.moveTo(centerX - 30, centerY - 6);
  ctx.lineTo(centerX + 30, centerY - 6);
  ctx.moveTo(centerX - 30, centerY + 6);
  ctx.lineTo(centerX + 30, centerY + 6);
  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 0.8;
  ctx.stroke();
  
  // Business name
  ctx.font = 'bold 9px Arial';
  const displayName = businessName.length > 18 ? businessName.slice(0, 18) : businessName;
  ctx.fillText(displayName, centerX, centerY);
  
  // CSC ID
  ctx.font = '8px Arial';
  ctx.fillText('CSC ID', centerX, centerY + 14);
  ctx.font = 'bold 8px Arial';
  ctx.fillText(cscId || 'N/A', centerX, centerY + 23);
  
  return canvas.toDataURL('image/png');
}

export async function generateA4InvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): Promise<jsPDF> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = 15;

  const formatCurrency = (amount: number) => `Rs. ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ===== HEADER SECTION - Logo on LEFT, Details on LEFT, TAX INVOICE on RIGHT =====
  const logoSize = 18;
  let logoLoaded = false;
  
  // Logo on left if enabled
  if (settings.show_logo !== false && tenant.logo_url) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = tenant.logo_url!;
      });
      doc.addImage(img, 'JPEG', margin, yPos, logoSize, logoSize);
      logoLoaded = true;
    } catch (e) {
      console.log('Could not load logo');
    }
  }

  // Business details on LEFT (next to logo)
  const textX = logoLoaded ? margin + logoSize + 4 : margin;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(tenant.name || 'VLE Plus', textX, yPos + 4);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  
  let detailY = yPos + 8;
  
  if (tenant.vle_id) {
    doc.text(`CSC ID: ${tenant.vle_id}`, textX, detailY);
    detailY += 3.5;
  }
  
  if (tenant.address) {
    const shortAddr = tenant.address.length > 50 ? tenant.address.slice(0, 50) + '...' : tenant.address;
    doc.text(shortAddr, textX, detailY);
    detailY += 3.5;
  }

  // Phone with icon
  if (tenant.phone) {
    // Draw phone icon (simple circle with receiver shape)
    const iconY = detailY - 2;
    doc.setDrawColor(80, 80, 80);
    doc.setFillColor(80, 80, 80);
    doc.circle(textX + 1.5, iconY, 1.2, 'F');
    doc.setFontSize(8);
    doc.text(`Tel: ${tenant.phone}`, textX + 4, detailY);
    
    // Email with icon (if exists, on same line)
    if (tenant.email) {
      const emailX = textX + 4 + doc.getTextWidth(`Tel: ${tenant.phone}`) + 4;
      // Draw envelope icon (simple rectangle)
      doc.setDrawColor(80, 80, 80);
      doc.rect(emailX, iconY - 0.8, 2.5, 1.6);
      doc.line(emailX, iconY - 0.8, emailX + 1.25, iconY + 0.2);
      doc.line(emailX + 2.5, iconY - 0.8, emailX + 1.25, iconY + 0.2);
      doc.text(`${tenant.email}`, emailX + 4, detailY);
    }
    detailY += 3.5;
  } else if (tenant.email) {
    // Only email, no phone
    const iconY = detailY - 2;
    doc.setDrawColor(80, 80, 80);
    doc.rect(textX, iconY - 0.8, 2.5, 1.6);
    doc.line(textX, iconY - 0.8, textX + 1.25, iconY + 0.2);
    doc.line(textX + 2.5, iconY - 0.8, textX + 1.25, iconY + 0.2);
    doc.text(`${tenant.email}`, textX + 4, detailY);
    detailY += 3.5;
  }

  // TAX INVOICE on RIGHT side
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(234, 88, 12); // Orange color
  doc.text('TAX INVOICE', pageWidth - margin, yPos + 4, { align: 'right' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`${sale.invoice_number || 'INV-XXXXX'}`, pageWidth - margin, yPos + 10, { align: 'right' });
  doc.text(`Date: ${format(new Date(sale.created_at), 'dd/MM/yyyy')}`, pageWidth - margin, yPos + 14, { align: 'right' });
  
  doc.setTextColor(0, 0, 0);
  yPos = Math.max(yPos + logoSize + 4, detailY + 4);

  // ===== BILL TO SECTION =====
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, yPos, pageWidth - 2 * margin, 12, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('Bill To:', margin + 2, yPos + 4);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(sale.customer_name || 'Walk-in Customer', margin + 2, yPos + 8);
  
  if (sale.customer_phone) {
    doc.text(`Mobile: ${sale.customer_phone}`, margin + 60, yPos + 8);
  }
  
  yPos += 15;

  // ===== ITEMS TABLE =====
  const applyGst = settings.gst_enabled && settings.apply_gst_on_invoices;
  const cgstPercent = settings.cgst_percent || 0;
  const sgstPercent = settings.sgst_percent || 0;

  let subtotalBeforeDiscount = 0;
  let itemLevelDiscount = 0;

  const tableData = items.map((item, index) => {
    const baseAmount = item.unit_price * item.quantity;
    const itemDiscount = item.discount || 0;
    
    subtotalBeforeDiscount += baseAmount;
    itemLevelDiscount += itemDiscount;

    return [
      (index + 1).toString(),
      item.service_name,
      item.quantity.toString(),
      `Rs. ${item.unit_price.toFixed(0)}`,
      itemDiscount > 0 ? `Rs. ${itemDiscount.toFixed(0)}` : '-',
      `Rs. ${item.total_price.toFixed(0)}`,
    ];
  });

  // Use sale-level total_discount if item-level discounts are 0
  const totalDiscount = itemLevelDiscount > 0 ? itemLevelDiscount : (sale.total_discount || 0);

  autoTable(doc, {
    startY: yPos,
    head: [['#', 'Service', 'Qty', 'Rate', 'Discount', 'Amount']],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [30, 58, 95],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
    },
    bodyStyles: {
      fontSize: 8,
      lineWidth: 0.1,
      lineColor: [200, 200, 200],
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 15, halign: 'center' },
      3: { cellWidth: 25, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 28, halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 6;

  // ===== BOTTOM SECTION - 3 Column Layout =====
  const bottomTableY = yPos;
  const bottomHeight = 55;
  const colWidth = (pageWidth - 2 * margin) / 3;

  // Draw border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(margin, bottomTableY, pageWidth - 2 * margin, bottomHeight);

  // Vertical lines
  doc.line(margin + colWidth, bottomTableY, margin + colWidth, bottomTableY + bottomHeight);
  doc.line(margin + colWidth * 2, bottomTableY, margin + colWidth * 2, bottomTableY + bottomHeight);

  // ===== LEFT COLUMN - Amount in Words + Bank Details =====
  let leftY = bottomTableY + 5;
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text('Amount in Words:', margin + 2, leftY);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(234, 88, 12); // Orange
  const amountInWords = numberToWords(sale.total_amount);
  const wrappedWords = doc.splitTextToSize(amountInWords, colWidth - 6);
  doc.text(wrappedWords.slice(0, 2), margin + 2, leftY + 4);
  
  leftY += 4 + (Math.min(wrappedWords.length, 2) * 3.5) + 4;
  
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Bank Details:', margin + 2, leftY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  leftY += 3;
  
  if (settings.bank_details) {
    const bankLines = settings.bank_details.split('\n').slice(0, 4);
    bankLines.forEach((line) => {
      doc.text(line.substring(0, 35), margin + 2, leftY);
      leftY += 2.5;
    });
  }

  // ===== MIDDLE COLUMN - QR Code + UPI =====
  const midX = margin + colWidth + 2;
  const midCenterX = margin + colWidth + (colWidth / 2);
  
  // QR Code centered
  if (settings.qr_code_url) {
    try {
      const qrImg = new Image();
      qrImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
        qrImg.src = settings.qr_code_url!;
      });
      doc.addImage(qrImg, 'PNG', midCenterX - 12, bottomTableY + 5, 24, 24);
    } catch (e) {
      // Draw QR placeholder
      doc.setDrawColor(200, 200, 200);
      doc.rect(midCenterX - 12, bottomTableY + 5, 24, 24);
      doc.setFontSize(6);
      doc.setTextColor(150, 150, 150);
      doc.text('QR Code', midCenterX, bottomTableY + 18, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
  } else {
    // Show placeholder
    doc.setDrawColor(200, 200, 200);
    doc.rect(midCenterX - 12, bottomTableY + 5, 24, 24);
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text('QR Code', midCenterX, bottomTableY + 18, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  // UPI ID below QR
  if (settings.upi_id) {
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.text(settings.upi_id, midCenterX, bottomTableY + 33, { align: 'center' });
  }

  // ===== RIGHT COLUMN - Totals + Stamp + Signatory =====
  const rightX = margin + colWidth * 2 + 4;
  const rightEndX = pageWidth - margin - 4;
  let rightY = bottomTableY + 5;

  // Calculate GST amounts
  const amountAfterDiscount = subtotalBeforeDiscount - totalDiscount;
  const cgstAmount = applyGst ? (amountAfterDiscount * cgstPercent) / 100 : 0;
  const sgstAmount = applyGst ? (amountAfterDiscount * sgstPercent) / 100 : 0;
  const totalGst = cgstAmount + sgstAmount;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  
  // Subtotal
  doc.text('Subtotal:', rightX, rightY);
  doc.text(formatCurrency(subtotalBeforeDiscount), rightEndX, rightY, { align: 'right' });
  rightY += 4;

  // Discount (if any)
  if (totalDiscount > 0) {
    doc.setTextColor(220, 38, 38);
    doc.text('Discount:', rightX, rightY);
    doc.text(`-${formatCurrency(totalDiscount)}`, rightEndX, rightY, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    rightY += 4;
  }

  // GST Breakdown (if enabled)
  if (applyGst && totalGst > 0) {
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(6);
    doc.text(`CGST (${cgstPercent}%):`, rightX, rightY);
    doc.text(formatCurrency(cgstAmount), rightEndX, rightY, { align: 'right' });
    rightY += 3;
    
    doc.text(`SGST (${sgstPercent}%):`, rightX, rightY);
    doc.text(formatCurrency(sgstAmount), rightEndX, rightY, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    rightY += 4;
    doc.setFontSize(7);
  }

  // Grand Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Total:', rightX, rightY);
  doc.text(`Rs. ${sale.total_amount.toFixed(0)}`, rightEndX, rightY, { align: 'right' });
  rightY += 5;

  // Horizontal divider
  doc.setDrawColor(200, 200, 200);
  doc.line(rightX - 2, rightY, rightEndX + 2, rightY);
  rightY += 4;

  // Stamp in right column
  if (settings.stamp_enabled !== false) {
    const stampSize = 22;
    const stampX = rightEndX - stampSize - 5;
    
    if (settings.signature_url) {
      try {
        const stampImg = new Image();
        stampImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          stampImg.onload = resolve;
          stampImg.onerror = reject;
          stampImg.src = settings.signature_url!;
        });
        doc.addImage(stampImg, 'PNG', stampX, rightY, stampSize, stampSize);
      } catch (e) {
        console.log('Could not load stamp');
      }
    } else {
      // Auto-generated stamp with orange color
      const stampDataUrl = generateStampDataUrl(
        tenant.name || 'VLE Plus',
        tenant.vle_id || 'N/A',
        tenant.address?.split(',')[0] || 'India'
      );
      if (stampDataUrl) {
        doc.addImage(stampDataUrl, 'PNG', stampX, rightY, stampSize, stampSize);
      }
    }
    rightY += stampSize + 2;
  }

  // For: Business Name & Authorized Signatory
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(`For: ${tenant.name}`, rightEndX, bottomTableY + bottomHeight - 8, { align: 'right' });
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6);
  doc.text('Authorized Signatory', rightEndX, bottomTableY + bottomHeight - 4, { align: 'right' });

  // Customer Signature on left
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Customer Signature', margin + 4, bottomTableY + bottomHeight - 4);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(margin + 4, bottomTableY + bottomHeight - 8, margin + 40, bottomTableY + bottomHeight - 8);

  yPos = bottomTableY + bottomHeight + 4;

  // ===== SERVICES BRANDING SECTION =====
  const brandingEnabled = settings.branding_enabled !== false;
  const services = settings.invoice_services || [];
  const brandingText = settings.branding_text;
  
  if (brandingEnabled && (services.length > 0 || brandingText)) {
    // Calculate branding box height based on content
    const brandingBoxHeight = brandingText ? 20 : 14;
    
    // Services branding box with gradient-like effect
    doc.setFillColor(255, 247, 237); // Orange-50 bg
    doc.setDrawColor(251, 146, 60); // Orange-400 border
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, brandingBoxHeight, 2, 2, 'FD');
    
    let brandY = yPos + 4;
    
    if (services.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(194, 65, 12); // Orange-700
      doc.text('Our Services:', margin + 3, brandY);
      
      // Services as bullet-separated list with better styling
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(154, 52, 18); // Orange-800
      const serviceNames = services.slice(0, 8).map(s => s.name).join(' • ');
      const wrappedServices = doc.splitTextToSize(serviceNames, pageWidth - 2 * margin - 30);
      doc.text(wrappedServices[0], margin + 25, brandY);
      brandY += 5;
    }
    
    // For local language branding text (Hindi/Marathi/etc), render using canvas
    if (brandingText) {
      try {
        // Load font first if available
        if (document.fonts) {
          try {
            await document.fonts.load('14px "Noto Sans Devanagari"');
          } catch (fontError) {
            console.log('Font loading not supported or failed, continuing with fallback');
          }
        }
        
        const brandingCanvas = document.createElement('canvas');
        const brandingWidth = pageWidth - 2 * margin - 10;
        brandingCanvas.width = brandingWidth * 5; // Higher res for clarity
        brandingCanvas.height = 40;
        const ctx = brandingCanvas.getContext('2d');
        
        if (ctx) {
          // Clear canvas
          ctx.clearRect(0, 0, brandingCanvas.width, brandingCanvas.height);
          
          // Use Noto Sans Devanagari for Hindi/Marathi support
          ctx.font = 'italic 18px "Noto Sans Devanagari", "Noto Sans", "Segoe UI", Arial, sans-serif';
          ctx.fillStyle = '#ea580c'; // Orange-600
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Draw text
          ctx.fillText(brandingText, brandingCanvas.width / 2, brandingCanvas.height / 2);
          
          const brandingDataUrl = brandingCanvas.toDataURL('image/png');
          const imgHeight = 6;
          const imgWidth = brandingWidth - 4;
          doc.addImage(brandingDataUrl, 'PNG', margin + 5, brandY, imgWidth, imgHeight);
        }
      } catch (e) {
        // Fallback: Just show English part if Unicode rendering fails
        console.log('Could not render local language text, using fallback');
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6);
        doc.setTextColor(234, 88, 12);
        doc.text('Your Service Center - Your Service', pageWidth / 2, brandY + 2, { align: 'center' });
      }
    }
    
    yPos += brandingBoxHeight + 2;
  }

  // ===== FOOTER SECTION =====
  const footerY = pageHeight - 12;
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  const footer = settings.invoice_footer || 'Thank you for your business!';
  doc.text(footer, pageWidth / 2, footerY - 4, { align: 'center' });
  
  doc.setFontSize(6);
  doc.setTextColor(30, 58, 95);
  doc.text('Powered by VLEplus | www.vleplus.in', pageWidth / 2, footerY, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  return doc;
}

export async function downloadA4InvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): Promise<void> {
  const doc = await generateA4InvoicePDF(sale, items, tenant, settings);
  const fileName = `Invoice-${sale.invoice_number || 'DRAFT'}_${format(new Date(sale.created_at), 'yyyyMMdd')}.pdf`;
  doc.save(fileName);
}

export async function printA4InvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): Promise<void> {
  const doc = await generateA4InvoicePDF(sale, items, tenant, settings);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  
  if (printWindow) {
    printWindow.addEventListener('load', () => {
      printWindow.print();
    });
  }
}
